<template>
  <div class="sticky-price-panel" :class="{ 'collapsed': isCollapsed }">
    <button
      type="button"
      class="toggle-button"
      @click="toggleCollapse"
      :aria-expanded="(!isCollapsed).toString()"
      :aria-label="labels.toggleAriaLabel"
    >
      <i class="bi" :class="isCollapsed ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </button>
    <div class="panel-content">
      <div class="price-info">
        <div class="current-price">
          <span class="price-label">{{ labels.livePriceLabel }}</span>
          <span class="price-value" :class="{ 'flash': priceFlash }">{{ formatPrice(goldPrice) }} <span class="price-unit">{{ labels.currencyLabel }}</span></span>
        </div>
        <div class="price-change" :class="priceChangeClass">
          <span class="change-value">{{ priceChangePercent }}%</span>
          <i class="bi" :class="priceChangeIcon"></i>
        </div>
      </div>
      <div class="action-buttons">
        <button class="btn-buy" @click="navigateToBuy">
          <span class="btn-label">{{ labels.buyLabel }}</span>
          <span class="btn-price">{{ formatPrice(buyPrice) }} {{ labels.currencyLabel }}</span>
        </button>
        <button class="btn-sell" @click="navigateToSell">
          <span class="btn-label">{{ labels.sellLabel }}</span>
          <span class="btn-price">{{ formatPrice(sellPrice) }} {{ labels.currencyLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { formatNumber } from '@/utils/numberFormatter';
import { calculateDisplayTradePrices } from '@/utils/tradeSettings';

const PANEL_LABELS = Object.freeze({
  toggleAriaLabel: '\u0646\u0645\u0627\u06cc\u0634 \u06cc\u0627 \u0645\u062e\u0641\u06cc \u06a9\u0631\u062f\u0646 \u067e\u0646\u0644 \u062e\u0631\u06cc\u062f \u0648 \u0641\u0631\u0648\u0634',
  livePriceLabel: '\u0642\u06cc\u0645\u062a \u0644\u062d\u0638\u0647 \u0627\u06cc \u0637\u0644\u0627\u06cc 18 \u0639\u06cc\u0627\u0631\u00a0\u06af\u0631\u0645\u06cc',
  currencyLabel: '\u062a\u0648\u0645\u0627\u0646',
  buyLabel: '\u062e\u0631\u06cc\u062f',
  sellLabel: '\u0641\u0631\u0648\u0634',
});

export default {
  name: 'StickyPricePanel',
  data() {
    return {
      labels: PANEL_LABELS,
      goldPrice: 0,
      buyPrice: 0,
      sellPrice: 0,
      previousPrice: 0,
      priceChangePercent: 0,
      todayOpenPriceRial: null,
      isCollapsed: false,
      userPreference: null,
      priceFlash: false,
    };
  },
  computed: {
    priceChangeClass() {
      return this.priceChangePercent >= 0 ? 'positive-change' : 'negative-change';
    },
    priceChangeIcon() {
      return this.priceChangePercent >= 0 ? 'bi-arrow-up' : 'bi-arrow-down';
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      // Save user preference to localStorage
      localStorage.setItem('stickyPanelCollapsed', this.isCollapsed);
    },
    formatPrice(price) {
      // Persian digits with thousands separators
      return formatNumber(price || 0, true);
    },
    navigateToBuy() {
      // Redirect based on auth: if logged in go to Exchange with buy tab; else go to login
      const isLoggedIn = this.$store && this.$store.state && this.$store.state.userToken;
      if (isLoggedIn) {
        this.$router.push({ name: 'PanelUser-Exchange', query: { tab: 'buy' } });
      } else {
        this.$router.push({
          name: 'PanelUser-Login',
          query: {
            redirect: 'exchange',
            purchaseType: 'buy',
          },
        });
      }
    },
    navigateToSell() {
      // Redirect based on auth: if logged in go to Exchange with sell tab; else go to login
      const isLoggedIn = this.$store && this.$store.state && this.$store.state.userToken;
      if (isLoggedIn) {
        this.$router.push({ name: 'PanelUser-Exchange', query: { tab: 'sell' } });
      } else {
        this.$router.push({
          name: 'PanelUser-Login',
          query: {
            redirect: 'exchange',
            purchaseType: 'sell',
          },
        });
      }
    },
    getPrice() {
      // Debounce concurrent requests within this component
      if (this._priceReqInFlight) return;
      this._priceReqInFlight = true;

      const url = "/public/price/now";
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      axios
        .get(url, config)
        .then((response) => {
          const serverResponse = response.data.data || {};
          // Store previous price for subtle animation
          const prev = this.goldPrice;
          // Update current price (YekGram18 is in Toman)
          this.goldPrice = serverResponse.prices?.YekGram18 || 0;
          
          const basePrice = Number(serverResponse.prices?.YekGram18 || 0);
          const displayPrices = calculateDisplayTradePrices(basePrice);
          this.buyPrice = displayPrices.buyPrice;
          this.sellPrice = displayPrices.sellPrice;

          // Trigger a subtle flash when price changes
          if (prev && this.goldPrice !== prev) {
            this.priceFlash = true;
            setTimeout(() => (this.priceFlash = false), 400);
          }

          // Recompute 24h change if we have today's open (in Rial)
          this.computeDailyChangePercent();
        })
        .catch((error) => {
          console.error("Error fetching price data:", error);
        })
        .finally(() => {
          this._priceReqInFlight = false;
        });
    },
    computeDailyChangePercent() {
      // goldPrice is in Toman; today's open is in Rial -> convert now to Rial for comparison
      if (this.todayOpenPriceRial && this.goldPrice) {
        const nowRial = this.goldPrice * 10;
        const openRial = this.todayOpenPriceRial;
        const change = nowRial - openRial;
        // Guard against division by zero and NaN
        if (openRial > 0) {
          const percent = (change / openRial) * 100;
          // Normalize -0 to 0 and clamp to reasonable bounds
          const normalized = Math.abs(percent) < 0.005 ? 0 : percent;
          this.priceChangePercent = Number(normalized.toFixed(2));
        }
      }
    },
    getPriceToday() {
      // Fetch today's OHLC data from database
      const url = "/public/price/today-candel";
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      axios
        .get(url, config)
        .then((response) => {
          const serverResponse = response?.data?.data || {};
          // 's' is today's opening price in Rial (already multiplied by 10)
          const openRial = Number(serverResponse.s || 0);
          if (openRial > 0) {
            this.todayOpenPriceRial = openRial;
            this.computeDailyChangePercent();
          }
        })
        .catch((error) => {
          console.error("Error fetching today's price data from database:", error);
        });
    },
  },
  mounted() {
    // Check if user has a preference for collapsed state
    const savedPreference = localStorage.getItem('stickyPanelCollapsed');
    if (savedPreference !== null) {
      this.isCollapsed = savedPreference === 'true';
    }

    // Initial fetches
    this.getPrice();
    this.getPriceToday();

    // Intervals (5s for prices per requirement; 60s for today's open)
    this.priceUpdateInterval = setInterval(() => {
      this.getPrice();
    }, 5000);
    this.priceTodayInterval = setInterval(() => {
      this.getPriceToday();
    }, 60000);
  },
  beforeUnmount() {
    // Clear intervals when component is destroyed
    clearInterval(this.priceUpdateInterval);
    clearInterval(this.priceTodayInterval);
  },
};
</script>

<style scoped>
.sticky-price-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(38, 37, 41, 0.95);
  border-top: 1px solid var(--bs-gold-light-transparent);
  z-index: 1000;
  transition:
    transform 0.3s ease-in-out,
    bottom 0.3s ease,
    left 0.3s ease,
    right 0.3s ease,
    border-radius 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
  direction: rtl;
  text-align: right;
}

.toggle-button {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 20px;
  background-color: rgba(38, 37, 41, 0.95);
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-top: 1px solid var(--bs-gold-light-transparent);
  border-left: 1px solid var(--bs-gold-light-transparent);
  border-right: 1px solid var(--bs-gold-light-transparent);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.toggle-button i {
  color: var(--bs-gold-light);
}

.panel-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  max-height: 80px;
  opacity: 1;
  transform: translateY(0);
  transition: transform 300ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 300ms ease, max-height 300ms ease, padding 300ms ease;
  will-change: transform, opacity;
}

.collapsed .panel-content {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  padding-top: 0;
  padding-bottom: 0;
  transform: translateY(100%);
}

.price-info {
  display: flex;
  flex-direction: column;
}

.current-price {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 0.9rem;
}

.price-label {
  font-weight: normal;
}

.price-value {
  font-weight: bold;
  color: var(--bs-gold-light);
}

.price-unit {
  font-size: 0.8rem;
  font-weight: normal;
  color: white;
}

.price-change {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  margin-top: 5px;
}

.price-change i {
  font-size: 0.9rem;
}

.price-value.flash {
  animation: flashColor 0.4s ease;
}

@keyframes flashColor {
  0% { color: var(--bs-gold-light); }
  50% { color: #ffffff; }
  100% { color: var(--bs-gold-light); }
}

.positive-change {
  color: var(--bs-success);
}

.negative-change {
  color: var(--bs-danger);
}

.action-buttons {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.btn-buy, .btn-sell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 10px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  text-align: center;
}

.btn-label {
  font-size: 0.9rem;
  font-weight: bold;
  line-height: 1.2;
}

.btn-price {
  font-size: 0.75rem;
  font-weight: normal;
  opacity: 0.9;
  margin-top: 2px;
  line-height: 1.1;
}

.btn-buy {
  background-color: var(--bs-success);
  color: white;
}

.btn-sell {
  background-color: var(--bs-danger);
  color: white;
}

.btn-buy:hover, .btn-sell:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* Responsive styles */
@media (max-width: 768px) {
  .sticky-price-panel {
    display: none;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .sticky-price-panel {
    display: none;
  }
}
</style>
